// Free-text role matching (CLIENT preview) — MIRROR of server/utils/role-match-utils.js in
// chaos-player-poc. Resolves a role the setter TYPED (a job title, an abbreviation, however they say
// it) to the scenario's role vocabulary. The server is authoritative (it also has an optional semantic
// LLM fallback and resolves at compose); this client copy powers the live "Scored on" preview under the
// Players roster. Deterministic tiers only: exact -> normalized (core token) -> synonym (curated
// IR-role groups). No semantic/LLM tier here. Keep in lockstep with the server module.

// Case-folded key for matching a role name (both sides normalized).
const roleKey = (r) =>
  String(r ?? "")
    .trim()
    .toLowerCase()

// Seniority / title words stripped when reducing a role or job title to its CORE. "VP of Operations"
// and "Head of Operations" both reduce to {operations}.
export const ROLE_TITLE_WORDS = new Set([
  "vp",
  "svp",
  "evp",
  "avp",
  "vice",
  "president",
  "chief",
  "officer",
  "head",
  "director",
  "sr",
  "senior",
  "jr",
  "junior",
  "deputy",
  "manager",
  "mgr",
  "lead",
  "leader",
  "global",
  "regional",
  "associate",
  "assistant",
  "principal",
  "of",
  "the",
])

// Generic "which unit" words that carry no identity on their own — dropped so "Operations Team",
// "Operations Dept" and "Operations" all reduce to {operations}.
export const ROLE_GENERIC_WORDS = new Set([
  "team",
  "teams",
  "dept",
  "department",
  "group",
  "office",
  "unit",
  "division",
  "staff",
  "personnel",
  "function",
  "and",
  "for",
  "a",
  "an",
])

// "Run the whole exercise" intents — never a scored scenario role. They short-circuit to
// whole-incident UNLESS the scenario genuinely declares one as a role (exact/normalized/synonym wins).
export const ROLE_SCOPE_WORDS = new Set([
  "all",
  "all roles",
  "everyone",
  "everybody",
  "whole incident",
  "the whole team",
  "everything",
  "facilitator",
  "facilitators",
  "incident commander",
  "commander",
  "coordinator",
  "observer",
])

// Curated synonym GROUPS for common incident-response roles. Two phrases match on the synonym tier
// when they land in the same group. Kept identical to the server seed.
export const ROLE_SYNONYM_GROUPS = [
  ["legal", "counsel", "general counsel", "legal counsel", "attorney", "lawyer", "legal team"],
  [
    "security",
    "soc",
    "soc team",
    "cyber",
    "cybersecurity",
    "infosec",
    "ciso",
    "security operations",
    "security operations center",
    "incident response",
    "ir",
    "firewall",
    "waf",
    "web application firewall",
    "application firewall",
    "app firewall",
    "network security",
    "netsec",
    "perimeter",
    "perimeter security",
    "blue team",
  ],
  ["it", "information technology", "helpdesk", "help desk", "sysadmin", "system administrator", "cio", "infrastructure"],
  ["finance", "accounting", "treasury", "cfo", "ap", "accounts payable"],
  ["operations", "ops", "operational", "coo"],
  ["executive", "exec", "leadership", "ceo", "c suite", "board", "senior leadership"],
  ["communications", "comms", "public relations", "pr", "media relations", "press"],
  ["hr", "human resources", "people", "people team"],
  ["privacy", "data protection", "dpo", "compliance"],
]

// term -> Set of group indexes it belongs to
const SYNONYM_INDEX = (() => {
  const idx = new Map()
  ROLE_SYNONYM_GROUPS.forEach((groupTerms, gi) => {
    for (const term of groupTerms) {
      const k = roleKey(term)
      if (!idx.has(k)) idx.set(k, new Set())
      idx.get(k).add(gi)
    }
  })
  return idx
})()

// "Platform Incident Coordination (PIC)": a parenthetical that is exactly the initials of the role"s
// other words (all words, without of/and/the, or the core words) is the same name again, not a
// qualifier, so it is dropped before matching — at setup only (matchRoleToScenario `initials`/`expand`),
// so compose-time resolution of existing exercises is unchanged. Any other parenthetical is kept.
const INITIALS_SKIP = new Set(["of", "and", "the", "for", "a", "an", "&"])
const wordsOf = (s) =>
  String(s ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
const initialsOf = (words) => words.map((w) => w[0]).join("")
export const stripInitialsParenthetical = (s) => {
  // Role titles are short; bounded input and no leading \s* keep the match linear (server mirror).
  const text = String(s ?? "")
  if (text.length > 200) return text
  const stripped = text.replace(/\(([^()]{0,60})\)/g, (whole, inner, offset) => {
    if (!/^[\sA-Za-z.&]+$/.test(inner)) return whole
    const letters = inner.replace(/[^A-Za-z]/g, "").toLowerCase()
    if (letters.length < 2) return whole
    const rest = wordsOf(text.slice(0, offset) + " " + text.slice(offset + whole.length))
    const candidates = [
      initialsOf(rest),
      initialsOf(rest.filter((w) => !INITIALS_SKIP.has(w))),
      initialsOf(rest.filter((w) => !ROLE_TITLE_WORDS.has(w) && !ROLE_GENERIC_WORDS.has(w))),
    ]
    return candidates.includes(letters) ? "" : whole
  })
  return stripped === text ? text : stripped.replace(/[ \t]{2,}/g, " ").trim()
}

// Lowercase, strip punctuation, collapse whitespace. The one normal form both sides share.
export const normalizeRolePhrase = (s) =>
  String(s ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()

// Common abbreviations spelled out (lockstep with server ROLE_ABBREVIATIONS). Only the server's Roles
// check uses `expand`; the save-time mirrors below stay strict without it, like the server's save.
export const ROLE_ABBREVIATIONS = Object.freeze({
  infosec: "information security",
  cybersec: "cyber security",
  it: "information technology",
  hr: "human resources",
  pr: "public relations",
  comms: "communications",
  ops: "operations",
  soc: "security operations center",
  ir: "incident response",
  ciso: "chief information security officer",
  cio: "chief information officer",
  cfo: "chief financial officer",
  coo: "chief operating officer",
  ceo: "chief executive officer",
  cto: "chief technology officer",
  dpo: "data protection officer",
  mgmt: "management",
})

// The identity-bearing tokens of a role/title: normalized, minus title and generic words.
// `expand`: abbreviations spelled out first.
export const roleCoreTokens = (s, { expand = false } = {}) =>
  normalizeRolePhrase(s)
    .split(" ")
    .flatMap((t) => (expand && Object.prototype.hasOwnProperty.call(ROLE_ABBREVIATIONS, t) ? ROLE_ABBREVIATIONS[t].split(" ") : [t]))
    .filter((t) => t && !ROLE_TITLE_WORDS.has(t) && !ROLE_GENERIC_WORDS.has(t))

const synonymGroupsFor = (phrase) => {
  const groups = new Set()
  const whole = SYNONYM_INDEX.get(roleKey(normalizeRolePhrase(phrase)))
  if (whole) for (const g of whole) groups.add(g)
  for (const tok of roleCoreTokens(phrase)) {
    const tg = SYNONYM_INDEX.get(roleKey(tok))
    if (tg) for (const g of tg) groups.add(g)
  }
  return groups
}

// Is `requested` a pure "run everything" intent?
export const isWholeIncidentIntent = (requested) => ROLE_SCOPE_WORDS.has(normalizeRolePhrase(requested))

// Mirror of the server's isCoordinatorRole (role-match-utils.js): a role that coordinates the whole
// response (incident manager / commander, EIM, IR lead, crisis manager) is scored on the whole incident.
// The coordinating word must be the HEAD of the title — not a role that supports one.
const COORDINATOR_HEAD_RE =
  /(?:^|\s)(?:enterprise\s+|major\s+|security\s+|cyber\s+)?(?:incident|crisis|emergency)(?:\s+(?:response|management))?(?:\s+team)?\s+(?:manager|commander|coordinator|director|lead|leader|controller)$/
const COORDINATOR_WHOLE_RE =
  /^(?:(?:enterprise|major)\s+)?incident\s+management$|^(?:head|director|manager)\s+of\s+(?:incident|crisis|emergency)\s+(?:response|management)$|^(?:eim|ir lead|ir manager|imt lead|cmt lead|incident commander|ic)$/
const NOT_COORDINATOR_RE = /\b(?:liaison|scribe|analyst|assistant|support|communications?|comms|to|for|deputy\s+to)\b/
export const isCoordinatorRole = (role) => {
  const raw = String(role ?? "").toLowerCase()
  if (!raw.trim()) return false
  const initials = (raw.match(/\(([a-z]{2,6})\)/) ?? [])[1] ?? ""
  const text = raw
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
  if (NOT_COORDINATOR_RE.test(text)) return false
  return COORDINATOR_HEAD_RE.test(text) || COORDINATOR_WHOLE_RE.test(text) || (initials === "eim" && /incident/.test(text))
}

// Match ONE requested role phrase to the best scenario role by the deterministic tiers.
// Returns { role, tier } (scenario's canonical spelling) or null.
// strict: exact and identical-core-words only (no synonym tier). Every exercise-setup caller uses it
// (design 2026-10-29 §4): the synonym tier matched on any shared word, so anything strict can't settle
// is left to the Roles check, where the server's AI suggests and the person confirms.
export const matchRoleToScenario = ({ requested: typedRequested, scenarioRoles, strict = false, expand = false, initials = false }) => {
  // Setup only: a trailing "(PIC)" that is the role"s own initials is dropped (stripInitialsParenthetical).
  const requested = expand || initials ? stripInitialsParenthetical(typedRequested) : typedRequested
  const roles = Array.isArray(scenarioRoles) ? scenarioRoles.filter((r) => String(r ?? "").trim()) : []
  // The scenario's own name, typed as written, always wins — before stripping "(SOC)" (a role named
  // "Security Operations Center (SOC)") and before the acronym-ambiguity check (a declared "IR").
  const typedN = normalizeRolePhrase(typedRequested)
  if (typedN) for (const r of roles) if (normalizeRolePhrase(r) === typedN) return { role: r, tier: "exact" }
  const n = normalizeRolePhrase(requested)
  if (!n || roles.length === 0) return null
  // A bare acronym that is the initials of SEVERAL of the scenario"s roles ("HR" with both "Human
  // Resources" and "Hazard Response") is ambiguous: no deterministic match, not even through the
  // curated expansion — the Roles check"s AI step decides from each role"s duties.
  if (expand && /^[a-z]{2,6}$/.test(n) && roles.filter((r) => initialsOf(roleCoreTokens(r)) === n).length > 1) return null

  // Tier 1 — exact
  // The same setup-only stripping on the scenario side: "Head of Security Operations Center (SOC)" vs
  // "Security Operations Center (SOC)" compares like for like; the scenario's own name is returned.
  const side = (r) => (expand || initials ? stripInitialsParenthetical(r) : r)
  for (const r of roles) if (normalizeRolePhrase(side(r)) === n) return { role: r, tier: "exact" }

  // Tier 2 — normalized: the UNIQUE role whose CORE token set EQUALS the requested core set. Equality,
  // NOT subset — so "VP of Operations" -> "Operations Team", but a bare "Operations" does NOT
  // order-dependently pick the first of {"Operations Team","Security Operations Center"}. Several roles
  // sharing a core -> ambiguous -> fall through. (Lockstep with server role-match-utils.js.)
  const coreSetKey = (s) => [...new Set(roleCoreTokens(s, { expand }).map(roleKey))].sort().join("|")
  const icKey = coreSetKey(requested)
  if (icKey) {
    const eq = roles.filter((r) => coreSetKey(side(r)) === icKey)
    if (eq.length === 1) return { role: eq[0], tier: "normalized" }
  }

  // Setup only (expand): a bare acronym ("HR", "PIC") is read in THIS scenario"s context — it points
  // at the UNIQUE role whose core-word initials equal it ("Hazard Response" or "Human Resources",
  // whichever the scenario has; title/team words aren"t counted). Returned as tier "acronym", which the
  // Roles check shows as "Matched (acronym)" (applied, undoable) — never a silent strict match.
  if (expand && /^[a-z]{2,6}$/.test(n)) {
    const acr = roles.filter((r) => initialsOf(roleCoreTokens(r)) === n)
    if (acr.length === 1) return { role: acr[0], tier: "acronym" }
  }

  if (strict) return null

  // Tier 3 — curated synonym groups. Ambiguity-safe: only wins when EXACTLY ONE scenario role shares a group.
  const reqGroups = synonymGroupsFor(requested)
  if (reqGroups.size) {
    const hits = roles.filter((r) => {
      for (const g of synonymGroupsFor(r)) if (reqGroups.has(g)) return true
      return false
    })
    if (hits.length === 1) return { role: hits[0], tier: "synonym" }
  }

  return null
}

// Resolve requested roles (free text) to canonical scenario roles — deterministic tiers only.
// Returns { resolved: string[] (deduped), matches: [{requested, role, tier}], unmatched: string[] }.
// Empty `resolved` means whole-incident. The server may still resolve an unmatched one via its semantic (LLM) tier.
// `initials`: the setup/save-time mirrors drop a "(PIC)" that is the role's own initials (server mapTypedRole).
export const resolveSoloRolesSync = ({ requestedRoles, scenarioRoles, strict = false, initials = false } = {}) => {
  const requested = Array.isArray(requestedRoles) ? requestedRoles.filter((r) => String(r ?? "").trim()) : []
  const roles = Array.isArray(scenarioRoles) ? scenarioRoles.filter((r) => String(r ?? "").trim()) : []
  const resolvedKeys = new Set()
  const resolved = []
  const matches = []
  const unmatched = []
  for (const req of requested) {
    const hit = matchRoleToScenario({ requested: req, scenarioRoles: roles, strict, initials })
    if (hit && hit.role) {
      const k = roleKey(hit.role)
      if (!resolvedKeys.has(k)) {
        resolvedKeys.add(k)
        resolved.push(hit.role)
      }
      matches.push({ requested: req, role: hit.role, tier: hit.tier })
    } else {
      unmatched.push(req)
    }
  }
  return { resolved, matches, unmatched }
}

// What a typed role can map to AT SETUP (lockstep with the server's scenarioRoleVocabulary): the
// scenario's LISTED roles (Info "Roles", split on commas or new lines; a prose roster is skipped
// entirely), its stored roles, and the roles its scored actions are tagged with when the scenario's
// actions are loaded on this path (scenario.actions / scenario.states — the wizard's scenario list
// usually doesn't carry them; the server's save-time mapping always includes tags and is what scoring
// uses). Deduped case-insensitively, listed spelling wins; comma-glued composites skipped.
const isCleanRoleTitle = (role) => {
  const r = String(role ?? "").trim()
  if (r.length < 2 || r.length > 60 || r.split(/\s+/).length > 8) return false
  let depth = 0
  for (const ch of r) {
    if (ch === "(") depth += 1
    else if (ch === ")") depth -= 1
    if (depth < 0) return false
  }
  return depth === 0
}
// A fragment that reads like a sentence about people, not a role title ("A Principal Engineer",
// "10+ years", "leading IR") marks the whole Roles field as a prose roster. Lockstep with the server.
const PROSE_FRAGMENT_RE = /^(?:a|an|the)\s|\d\s*\+|\b(?:years?|experience|leading|responsible|who)\b/i
export const listedScenarioRoles = (scenario) => {
  const parts = String(scenario?.info?.Roles ?? "")
    .split(/[,\n]/)
    .map((r) => r.trim())
    .filter(Boolean)
  return parts.length && parts.every((r) => isCleanRoleTitle(r) && !PROSE_FRAGMENT_RE.test(r)) ? parts : []
}
const isScoredAction = (a) => ["golden", "correct"].includes(String(a?.res ?? "").toLowerCase()) || Boolean(a?.fastestPath)
export const taggedScenarioRoles = ({ actions = null, states = null } = {}) => {
  const list = (v) => (Array.isArray(v) ? v : Object.values(v && typeof v === "object" ? v : {}))
  const out = []
  for (const a of list(actions)) if (isScoredAction(a) && Array.isArray(a?.roles)) out.push(...a.roles)
  for (const st of list(states)) {
    for (const a of st?.supportedActions ?? []) if (isScoredAction(a) && Array.isArray(a?.roles)) out.push(...a.roles)
    if (st?.assignedTo) out.push(st.assignedTo)
  }
  return out
}
export const scenarioRoleCandidates = (scenario) => {
  const stored = Array.isArray(scenario?.roles) ? scenario.roles : scenario?.roles ? [scenario.roles] : []
  const tagged = taggedScenarioRoles({ actions: scenario?.actions, states: scenario?.states })
  const seen = new Map()
  for (const r of [...listedScenarioRoles(scenario), ...stored, ...tagged]) {
    const v = String(r ?? "").trim()
    if (!v || v.includes(",")) continue
    const k = roleKey(v)
    if (!seen.has(k)) seen.set(k, v)
  }
  return [...seen.values()]
}
